<template>
    <div class="ah-page project-accounting-statement-page">
        <div class="page-header bottom-margin">
            <div class="ah-container">
                <div class="title-row">
                    <div class="title">
                        <h1>專案綜合損益表</h1>
                    </div>
                    <div class="actions">
                        <el-button icon="el-icon-refresh" @click="updateData"></el-button>
                        <el-button @click="onPrint">列印</el-button>
                        <el-button size="" @click="onExport">匯出Excel</el-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="ah-container">
            <div class="ah-panel no-print">
                <div class="panel-body">
                    <search-form>
                        <template v-if="displayMode === 1">
                            <search-form-item label="傳票日期範圍">
                                <ahDropdownDatePicker v-model="dateRange" @change="updateData" />
                            </search-form-item>
                            <search-form-item label="金額為 0 的科目">
                                <el-radio v-model="isShowZeroItems" :label="true" @change="updateData">顯示</el-radio>
                                <el-radio v-model="isShowZeroItems" :label="false" @change="updateData">隱藏</el-radio>
                            </search-form-item>
                        </template>
                        <template v-if="displayMode === 2">
                            <search-form-item label="傳票年度">
                                <el-select v-model="queryYear" @change="fetchMonthlyData">
                                    <el-option v-for="year in yearOptions" :key="year" :label="`${year - 1911} 年`" :value="year" />
                                </el-select>
                            </search-form-item>
                            <search-form-item label="金額為 0 的科目">
                                <el-radio v-model="isShowZeroItems" :label="true" @change="fetchMonthlyData">顯示</el-radio>
                                <el-radio v-model="isShowZeroItems" :label="false" @change="fetchMonthlyData">隱藏</el-radio>
                            </search-form-item>
                        </template>
                        <search-form-item label="專案">
                            <el-select v-model="selectedProject" value-key="poo" placeholder="請選擇專案" clearable multiple filterable reserve-keyword style="width: 600px" @change="onSelectProjectChange">
                                <el-option v-for="item in projectList" :key="item.poo" :label="item.poo + ' ' + item.nam" :value="item" style="max-width: 600px" />
                            </el-select>
                        </search-form-item>
                    </search-form>
                </div>
            </div>
            <div class="ah-panel">
                <div class="panel-body">
                    <template v-if="displayMode === 1">
                        <div class="accounting-statement-normal-table-wrapper">
                            <NormalDisplay
                                :projectList="selectedProject"
                                :tableData="tableData"
                                :isLoading="isLoading"
                                :todayDateTxt="todayDateTxt"
                                :startDateTxt="startDateTxt"
                                :endDateTxt="endDateTxt"
                                :hideChildItems="hideChildItems"
                                is-project
                                @click="onClickItem"
                            />
                        </div>
                    </template>
                    <template v-else>
                        <MonthlyDisplay
                            :tableData="monthlyTableData"
                            :isLoading="isLoading"
                            :todayDateTxt="todayDateTxt"
                            :startDateTxt="startDateTxt"
                            :endDateTxt="endDateTxt"
                            :hideChildItems="hideChildItems"
                            :year="queryYear"
                            @click="onClickItem"
                        />
                    </template>
                </div>
            </div>
        </div>
        <DetailDialog
            :visible="detailDialog.visible"
            :StartDate="queryParams.StartDate"
            :EndDate="queryParams.EndDate"
            :poo="queryParams.poo"
            :accno="detailDialog.accno"
            :accnam="detailDialog.accnam"
            @close="onDetailDialogClose"
        />
    </div>
</template>

<script>
import _ from 'lodash';
import qs from 'qs';
import moment from 'moment';
import ahDropdownDatePicker from '@/components/ui/ahDropdownDatePicker.vue';
import SearchForm from '@/components/ui/SearchForm/SearchForm.vue';
import SearchFormItem from '@/components/ui/SearchForm/SearchFormItem.vue';
import ExcelMixins from './ExcelMixins';
import NormalDisplay from './NormalDisplay.vue';
import MonthlyDisplay from './MonthlyDisplay.vue';

export default {
    name: 'ProjectAccountingStatementPage',
    mixins: [ExcelMixins],
    components: {
        NormalDisplay,
        MonthlyDisplay,
        ahDropdownDatePicker,
        SearchForm,
        SearchFormItem,
        DetailDialog: () => import('@/components/ui/ahAccountingGeneralLedgerDialog.vue'),
    },
    computed: {
        todayDateTxt() {
            return moment().format('YYYY年MM月DD日');
        },
        startDateTxt() {
            const DATE = moment(+this.queryParams.StartDate);
            return `${DATE.year() - 1911}年${DATE.format('MM月DD日')}`;
        },
        endDateTxt() {
            const DATE = moment(+this.queryParams.EndDate);
            return `${DATE.year() - 1911}年${DATE.format('MM月DD日')}`;
        },
        isStartEndDateSameYear() {
            return moment(Number(this.dateRange[0])).year() === moment(Number(this.dateRange[1])).year();
        },
    },
    data() {
        return {
            queryYear: moment().year(),
            yearOptions: [],
            openDate: null,
            selectedProject: [],
            projectList: [],

            displayMode: 1,
            isLoading: false,
            isShowZeroItems: true,
            dateRange: [null, null],
            tableData: [],
            monthlyTableData: [],
            hideChildItems: false,
            title: null,
            queryParams: {
                StartDate: null,
                EndDate: null,
                poo: undefined,
            },
            tableDataTemplate: [
                {
                    id: 'operatingRevenues',
                    name: '營業收入',
                    type: 1,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    dC_TYP: 2,
                    level: 0,
                },
                {
                    id: 'netIncomeTotal',
                    name: '營業收入淨額',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    isTotal: true,
                    sumBy: ['operatingRevenues'],
                    level: 0,
                },
                {
                    id: 'costofGoodsSold',
                    name: '銷貨成本',
                    type: 1,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    dC_TYP: 1,
                    level: 0,
                },
                {
                    id: 'manufacturingCosts',
                    name: '製造成本',
                    type: -1,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    dC_TYP: 1,
                    level: 0,
                },
                {
                    id: 'otherOperatingExpenses',
                    name: '其他營業成本',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    dC_TYP: 1,
                    level: 0,
                },
                {
                    id: 'costTotal',
                    name: '營業成本',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    isTotal: true,
                    sumBy: ['costofGoodsSold', 'manufacturingCosts', 'otherOperatingExpenses'],
                    level: 0,
                },
                {
                    id: 'profit',
                    name: '營業毛利',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    isCalculate: true,
                    method: ['-', 'netIncomeTotal', 'costTotal'],
                    level: 0,
                },
                {
                    id: 'operatingCost',
                    name: '營業費用',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    dC_TYP: 1,
                    level: 0,
                },
                {
                    id: 'income',
                    name: '營業收益',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    isCalculate: true,
                    method: ['-', 'profit', 'operatingCost'],
                    level: 0,
                },
                {
                    id: 'nonOperatingIncome',
                    name: '營業外收入',
                    type: -1,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    dC_TYP: 2,
                    level: 0,
                },
                {
                    id: 'nonOperatingExpenses',
                    name: '營業外支出',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    dC_TYP: 1,
                    level: 0,
                },
                {
                    id: 'nonOperatingNetIncome',
                    name: '營業外收入及支出總計',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    isCalculate: true,
                    method: ['-', 'nonOperatingIncome', 'nonOperatingExpenses'],
                    level: 0,
                },
                {
                    id: 'NetProfitOrLoss',
                    name: '稅前損益',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    isCalculate: true,
                    method: ['+', 'income', 'nonOperatingNetIncome'],
                    level: 0,
                },
                {
                    id: 'incomeTax',
                    name: '營利事業所得稅',
                    type: 1,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    dC_TYP: 1,
                    level: 0,
                },
                {
                    id: 'taxedProfitOrLoss',
                    name: '稅後淨利',
                    type: 0,
                    children: [],
                    amount: 0,
                    percentage: 0,
                    isCalculate: true,
                    method: ['-', 'NetProfitOrLoss', 'incomeTax'],
                    level: 0,
                },
            ],

            detailDialog: {
                accno: null,
                accnam: null,
                visible: false,
            },
        };
    },
    mounted() {
        this.init();
    },
    methods: {
        async init() {
            const today = moment();
            this.dateRange = [today.startOf('year').format('x'), today.endOf('year').format('x')];

            const START_YEAR = 2020;
            for (let y = this.queryYear; y >= START_YEAR; y -= 1) {
                this.yearOptions.push(y);
            }

            await this.fetchProjectList();
            await this.updateData();
        },
        async fetchProjectList() {
            const resp = await this.$api({
                url: 'AccountingSummaryReport/GetProjectList',
                method: 'GET',
            });

            this.projectList = resp.data.items;
        },
        async getYearlyOpenVoucher() {
            const OPEN_DATE = moment(+this.openDate);
            const SELECTED_END_DATE = moment(+this.queryParams.EndDate);
            const SELECTED_START_DATE = moment(+this.queryParams.StartDate);

            if (this.openDate && OPEN_DATE.year() === SELECTED_END_DATE.year() && SELECTED_START_DATE.isAfter(OPEN_DATE)) return;
            if (OPEN_DATE.year() === SELECTED_END_DATE.year() && SELECTED_START_DATE.isBefore(OPEN_DATE)) {
                this.$message({
                    showClose: true,
                    message: `${SELECTED_START_DATE.year()}年開帳日期為 ${OPEN_DATE.format('YYYY-MM-DD')}，已自動將查詢起日設為開帳日期。`,
                    type: 'warning',
                });
            }

            const resp = await this.$api({
                url: 'AccountingSummaryPage/QueryVouchers',
                method: 'GET',
                params: {
                    StartDateStart: moment(+this.queryParams.StartDate).startOf('year').format('x'),
                    StartDateEnd: moment(+this.queryParams.StartDate).endOf('year').format('x'),
                    TransactionType: 12,
                },
            });

            if (resp.data.totalNum) {
                this.openDate = _.head(resp.data.items)?.dat;
                this.dateRange = [this.openDate, this.dateRange[1]];
            } else this.openDate = null;

            this.updateQuery();
        },
        updateQuery() {
            this.queryParams.StartDate = _.head(this.dateRange);
            this.queryParams.EndDate = _.last(this.dateRange);
        },
        checkQueryValidate() {
            const OPEN_DATE = moment(+this.openDate);
            const SELECTED_DATE = moment(+this.queryParams.EndDate);
            if (this.openDate && SELECTED_DATE.isBefore(OPEN_DATE)) {
                this.$message({
                    showClose: true,
                    message: `${SELECTED_DATE.year()}年開帳日期為 ${OPEN_DATE.format('YYYY-MM-DD')}，請重新選擇日期範圍。`,
                    type: 'error',
                });
                return false;
            }
            return true;
        },
        async fetchMonthlyData() {
            this.dateRange = [moment().year(this.queryYear).startOf('year').format('x'), moment().year(this.queryYear).endOf('year').format('x')];
            this.updateQuery();
            await this.getYearlyOpenVoucher();

            try {
                const resp = await this.$api({
                    url: '/AccountingSummaryReport/QueryMultiMonthGaap',
                    method: 'GET',
                    params: { year: this.queryYear },
                });
                this.monthlyTableData = this.updateMonthlyTableData(resp.data);
            } finally {
                this.isLoading = false;
            }
        },
        async updateData() {
            if (!this.isStartEndDateSameYear) {
                this.$message({
                    showClose: true,
                    message: '不可跨年度查詢，請重新選擇日期範圍。',
                    type: 'error',
                });
                return;
            }
            this.updateQuery();
            this.isLoading = true;
            await this.getYearlyOpenVoucher();
            if (!this.checkQueryValidate()) return;

            try {
                const resp = await this.$api({
                    url: '/AccountingSummaryReport/QueryGaap',
                    method: 'GET',
                    paramsSerializer: (params) => qs.stringify(params, { indices: false, skipNulls: true }),
                    params: this.queryParams,
                });
                this.tableData = this.updateTableData(resp.data);
            } finally {
                this.isLoading = false;
            }
        },
        updateMonthlyTableData(yearData) {
            // 從資料模板複製一份
            const RETURN = _.cloneDeep(this.tableDataTemplate);

            yearData.forEach((monthData) => {
                const MONTH = monthData.month;

                RETURN.forEach((item) => {
                    const ITEM = item;
                    const MONTH_DATA_CHILDREN = monthData[ITEM.id];

                    if (MONTH_DATA_CHILDREN && MONTH_DATA_CHILDREN.length) {
                        MONTH_DATA_CHILDREN.forEach((o) => {
                            const FOUND = _.find(ITEM.children, ['id', o.accno]);
                            if (FOUND) {
                                FOUND[`amount-m${MONTH}`] = Math.round(o.ntTotalAmount);
                                FOUND[`percentage-m${MONTH}`] = 0;
                            } else {
                                ITEM.children.push({
                                    id: o.accno,
                                    name: o.accnam,
                                    [`amount-m${MONTH}`]: Math.round(o.ntTotalAmount),
                                    [`percentage-m${MONTH}`]: 0,
                                    isMinus: o.dC_TYP !== item.dC_TYP,
                                    level: 1,
                                });
                            }
                        });
                    } else if (ITEM.children.length) {
                        ITEM.children.forEach((child) => {
                            const CHILD = child;
                            CHILD[`amount-m${MONTH}`] = 0;
                            CHILD[`percentage-m${MONTH}`] = 0;
                        });
                    }

                    // 加入資料後處理方法
                    if (ITEM.isTotal && ITEM.sumBy) {
                        let sum = 0;
                        ITEM.sumBy.forEach((id) => {
                            sum += _.chain(RETURN)
                                .find(['id', id])
                                .get('children')
                                .sumBy((o) => (o.isMinus ? -o[`amount-m${MONTH}`] || 0 : o[`amount-m${MONTH}`] || 0))
                                .value();
                        });
                        ITEM[`amount-m${MONTH}`] = sum;
                    } else if (ITEM.isCalculate) {
                        const OPERATOR = ITEM.method[0];
                        const FIRST = _.find(RETURN, ['id', ITEM.method[1]])[`amount-m${MONTH}`];
                        const LAST = _.find(RETURN, ['id', ITEM.method[2]])[`amount-m${MONTH}`];

                        if (OPERATOR === '-') ITEM[`amount-m${MONTH}`] = FIRST - LAST;
                        else if (OPERATOR === '+') ITEM[`amount-m${MONTH}`] = FIRST + LAST;
                    } else if (ITEM.level === 0) {
                        ITEM[`amount-m${MONTH}`] = _.sumBy(ITEM.children, (o) => (o.isMinus ? -o[`amount-m${MONTH}`] || 0 : o[`amount-m${MONTH}`]) || 0);
                    }

                    ITEM.children = _.sortBy(ITEM.children, 'isMinus');
                });
            });

            // 計算總和
            RETURN.forEach((item) => {
                const ITEM = item;
                for (let m = 1; m <= 12; m += 1) {
                    ITEM.amount += ITEM[`amount-m${m}`] || 0;
                }

                ITEM.children.forEach((child) => {
                    const CHILD = child;
                    CHILD.amount = 0;
                    let zeroMonthCount = 0;
                    for (let m = 1; m <= 12; m += 1) {
                        // 項目金額加總
                        CHILD.amount += CHILD[`amount-m${m}`] || 0;

                        // 順便計算為0的月份數，供後續踢除顯示使用
                        if (!CHILD[`amount-m${m}`] || CHILD[`amount-m${m}`] === 0) {
                            zeroMonthCount += 1;
                        }
                    }
                    // 如果12個月都為零，則標記 allZero，供後續踢除顯示使用
                    if (zeroMonthCount >= 12) {
                        CHILD.allZero = true;
                    } else {
                        CHILD.allZero = false;
                    }
                });
            });

            // 踢除全部月份數字為0的科目
            if (!this.isShowZeroItems) {
                RETURN.forEach((item) => {
                    const ITEM = item;
                    ITEM.children = ITEM.children.filter((child) => child.allZero !== true);
                });
            }

            // RETURN.children 項目重新以 accno(id) 排序
            RETURN.forEach((item) => {
                const ITEM = item;
                ITEM.children = _.sortBy(ITEM.children, 'id');
            });

            return RETURN;
        },

        updateTableData(data) {
            // 從資料模板複製一份
            const RETURN = _.cloneDeep(this.tableDataTemplate);

            // 使用 groupNo 逐一找到對應項目，塞入 children
            _.forEach(data, (value, key) => {
                const tableItem = RETURN.find((n) => n.id === key);
                if (tableItem) {
                    // isShowZeroItems 為 false 時，排除金額為 0 的科目
                    const newChildren = data[tableItem.id].map((item) => ({
                        id: item.accno,
                        name: item.accnam,
                        amount: Math.round(item.ntTotalAmount),
                        percentage: 0,
                        isMinus: tableItem.dC_TYP !== item.dC_TYP,
                        level: 1,
                    }));

                    // 在 isShowZeroItems 為 false 時，只加入金額不為 0 的科目
                    if (this.isShowZeroItems) {
                        tableItem.children = tableItem.children.concat(newChildren);
                    } else {
                        const noneZeroChildren = newChildren.filter((item) => item.amount !== 0);
                        tableItem.children = tableItem.children.concat(noneZeroChildren);
                    }
                }
            });

            // 加入資料後處理方法
            RETURN.forEach((item, index) => {
                if (item.isTotal && item.sumBy) {
                    let sum = 0;
                    item.sumBy.forEach((id) => {
                        sum += _.chain(RETURN)
                            .find(['id', id])
                            .get('children')
                            .sumBy((o) => (o.isMinus ? -o.amount : o.amount))
                            .value();
                    });
                    RETURN[index].amount = sum;
                } else if (item.isCalculate) {
                    const OPERATOR = item.method[0];
                    const FIRST = _.find(RETURN, ['id', item.method[1]]).amount;
                    const LAST = _.find(RETURN, ['id', item.method[2]]).amount;

                    if (OPERATOR === '-') RETURN[index].amount = FIRST - LAST;
                    else if (OPERATOR === '+') RETURN[index].amount = FIRST + LAST;
                } else if (item.level === 0) {
                    RETURN[index].amount = _.sumBy(item.children, (o) => (o.isMinus ? -o.amount : o.amount));
                }

                RETURN[index].children = _.sortBy(item.children, 'isMinus');
            });

            const NET_INCOME_TOTAL = _.find(RETURN, ['id', 'netIncomeTotal']).amount;
            RETURN.forEach((item) => {
                const ITEM = item;

                ITEM.percentage = this.normalizeNumber(ITEM.amount / NET_INCOME_TOTAL);

                ITEM.children.forEach((child) => {
                    const c = child;
                    c.percentage = this.normalizeNumber(c.amount / NET_INCOME_TOTAL);
                });
            });

            return RETURN;
        },
        openAll() {
            this.hideChildItems = false;
            this.tableData.forEach((item) => {
                this.$refs.table.toggleRowExpansion(item, true);
            });
        },
        closeAll() {
            this.hideChildItems = true;
            this.tableData.forEach((item) => {
                this.$refs.table.toggleRowExpansion(item, false);
            });
        },
        onPrint() {
            window.print();
        },
        normalizeNumber(num) {
            return parseFloat(Math.round(num * 10000) / 100).toFixed(2);
        },

        onClickItem(item) {
            if (!item.level) return;

            this.detailDialog.accno = item.id;
            this.detailDialog.accnam = item.name;
            this.detailDialog.visible = true;
        },
        onDetailDialogClose(refresh) {
            this.detailDialog.visible = false;
            if (refresh) this.updateData();
        },

        onDisplayModeChange(val) {
            if (val === 1) {
                this.dateRange = [moment().year(this.queryYear).startOf('year').format('x'), moment().year(this.queryYear).endOf('year').format('x')];
                this.updateData();
            } else {
                this.queryYear = moment(+_.head(this.dateRange)).year();
                this.fetchMonthlyData();
            }
        },
        onSelectProjectChange() {
            this.queryParams.poo = this.selectedProject.length ? this.selectedProject.map((o) => o.poo) : null;
            this.updateData();
        },
    },
};
</script>
<style lang="scss">
@import "@/assets/scss/vars.scss";

.project-accounting-statement-page {
    .action-bar {
        margin: 0 0 15px 0;
        padding: 0 30px;
        display: flex;
        align-items: flex-end;
        gap: 20px;

        @media (max-width: 900px) {
            flex-direction: column;
            align-items: flex-start;
        }

        .query-item-block {
            display: flex;
            flex-direction: column;
            margin-right: 10px;
            gap: 5px;
        }
        .datepicker-label {
            font-size: 10pt;
            color: #888;
        }
    }

    @media print {
        .action-bar {
            display: none;
        }
    }

    .accounting-statement-normal-table-wrapper {
        @media screen {
            max-width: 1000px;
            margin: 0 auto 0 auto;
        }
    }
}
</style>
